import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Blocks, ListChecks, Play, Power, PowerOff, Wrench } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { ActionsEditorDialog } from './ActionsEditorDialog'
import {
  fetchWorkflows,
  runWorkflow,
  toggleWorkflow,
  AUTO_STR as S,
  type WorkflowRow,
} from './automationApi'

/**
 * A26.02 — Конструктор сценариев (workflow builder). Lists real workflows and
 * opens the action-pipeline editor (real `PUT .../actions`) to build a workflow's
 * steps; run-once (`.../run`) tests the build. Placeholder permission USERS_VIEW.
 */
export function WorkflowBuilderScreen() {
  const { toast } = useToast()
  const [editing, setEditing] = useState<WorkflowRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<WorkflowRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'trigger', header: S.trigger, type: 'text' },
      { key: 'actions', header: S.actions, type: 'number' },
      { key: 'enabled', header: S.enabled, type: 'text' },
      { key: 'last_run', header: S.lastRun, type: 'text' },
      { key: 'runs', header: S.runs, type: 'number' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<WorkflowRow>[]>(
    () => [
      {
        id: 'build',
        label: S.build,
        icon: <Wrench size={14} />,
        onSelect: (r) => setEditing(r),
      },
      {
        id: 'run',
        label: S.run,
        icon: <Play size={14} />,
        onSelect: async (r) => {
          try {
            await runWorkflow(r.id)
            toast(S.started, { tone: 'success' })
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
      {
        id: 'disable',
        label: S.disable,
        icon: <PowerOff size={14} />,
        danger: true,
        visible: (r) => r.enabled,
        onSelect: async (r) => {
          try {
            await toggleWorkflow(r.id, false)
            toast(S.toggled, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
      {
        id: 'enable',
        label: S.enable,
        icon: <Power size={14} />,
        visible: (r) => !r.enabled,
        onSelect: async (r) => {
          try {
            await toggleWorkflow(r.id, true)
            toast(S.toggled, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(
    (p: Parameters<typeof fetchWorkflows>[0]) => {
      void reloadKey
      return fetchWorkflows(p)
    },
    [reloadKey],
  )

  return (
    <>
      <PageHeader
        title={S.builder}
        screenId="A26.02"
        actions={
          <Link
            to="/automation/conditions"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <ListChecks size={13} />
            {S.conditions}
          </Link>
        }
      />
      <DataTable<WorkflowRow>
        screenId="A26.02"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Blocks size={28} strokeWidth={1.5} /> }}
      />
      <ActionsEditorDialog
        workflow={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </>
  )
}
